import * as FileSaver from 'file-saver'
import { fromPairs, isNil, sample } from 'lodash'
import * as PropTypes from 'prop-types'
import * as React from 'react'
import { Button } from 'react-bootstrap'
import * as ReactDOM from 'react-dom'
import { Helmet } from 'react-helmet'
import { addUrlProps, UrlQueryParamTypes, UrlUpdateTypes } from 'react-url-query'
import '../assets/App.css'

import BotForm from './BotForm'
import { allOptions, Bot, BotStyle, OptionContext } from './bottts'
import ComponentCode from './ComponentCode'
import ComponentImg from './ComponentImg'
import { SeededBottt } from './SeededBottt'
import Timer = NodeJS.Timer

interface Props {
  __render__?: string
  botStyle: BotStyle
  onChangeUrlQueryParams: (params: any, updateType: string) => void
  onChangeBotStyle: (botStyle: BotStyle) => void
}

const updateType = UrlUpdateTypes.pushIn
const urlPropsQueryConfig = {
  ...fromPairs(
    allOptions.map(option => [
      option.key,
      {
        type: UrlQueryParamTypes.string,
        updateType,
      },
    ]),
  ),
  botStyle: {
    type: UrlQueryParamTypes.string,
    updateType,
  },
}

interface State {
  displayComponentCode: boolean,
  displayComponentImg: boolean,
  intervalOn: boolean,
  intervalStore: number | Timer,
  imageURI: string,
}

function capitalizeFirstLetter (text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export class Main extends React.Component<Props, State> {
  static childContextTypes = {
    optionContext: PropTypes.instanceOf(OptionContext),
  }
  static defaultProps = {
    botStyle: BotStyle.Circle,
  }

  state = {
    displayComponentCode: false,
    displayComponentImg: false,
    intervalOn: false,
    intervalStore: 0,
    imageURI: '',
  }

  private botRef: Bot | null = null
  private canvasRef: HTMLCanvasElement | null = null
  private optionContext: OptionContext = new OptionContext(allOptions)

  getChildContext () {
    return { optionContext: this.optionContext }
  }

  componentWillReceiveProps (nextProps: Props) {
    this.updateOptionContext(nextProps)
  }

  componentWillMount () {
    this.optionContext.addValueChangeListener(this.onOptionValueChange)
    this.updateOptionContext(this.props)
  }

  componentDidMount () {
    const anyWindow = window as any
    setTimeout(() => {
      anyWindow.prerenderReady = true

      this.onImagePreview()
    }, 500)
  }

  componentWillUnmount () {
    this.optionContext.removeValueChangeListener(this.onOptionValueChange)
  }

  render () {
    const { botStyle } = this.props
    const { displayComponentCode, displayComponentImg } = this.state
    const title = 'Bottts Generator - Generate your own bottts!'
    // const imageURL = process.env.REACT_APP_IMG_RENDERER_URL + location.search
    const imageSize = 300
    // @ts-ignore
    const urlHref = `${document.location.href}`

    return (
      <main role='main'>
        <header className='header row clearfix'>
          <h2 className='col-12 col-md-6' style={{ color: '#d25cff' }}>
            bottts generator
          </h2>

          <div className='col-12 col-md-6'>
            <div className='d-flex justify-content-between'>
              <Button
                type='submit'
                bsStyle='info'
                onClick={this.onSeed}>
                <i className='fa fa-key'/> Seed
              </Button>

              <Button
                type='submit'
                bsStyle='info'
                onClick={this.onRandom}>
                <i className='fa fa-random'/> Random
              </Button>

              {!this.state.intervalOn ?
                <Button
                  type='submit'
                  bsStyle='info'
                  onClick={this.startRandomInterval}>
                  <i className='fa fa-play'/> Start
                </Button>
                :
                <Button
                  type='submit'
                  bsStyle='info'
                  onClick={this.stopRandomInterval}>
                  <i className='fa fa-stop'/> Stop
                </Button>
              }
            </div>
          </div>
        </header>
        <Helmet>
          <meta property='og:title' content={title}/>
          <meta property='og:site_name' content='Bottts Generator'/>
          <meta property='og:url' content={urlHref}/>
          <meta property='og:image' content={this.state.imageURI}/>
          <meta
            property='og:description'
            content='Bottts Generator is a free online tool for generating your own bot'
          />
          <meta name='twitter:card' content='summary_large_image'/>
          <meta name='twitter:site' content='Bottts Generator'/>
          <meta name='twitter:title' content={title}/>
          <meta name='twitter:image' content={this.state.imageURI}/>
          <meta name='twitter:url' content={urlHref}/>
        </Helmet>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <Bot ref={this.onBotRef} size={imageSize} botStyle={botStyle}/>
        </div>
        <BotForm
          optionContext={this.optionContext}
          botStyle={botStyle}
          displayingCode={displayComponentCode}
          displayingImg={displayComponentImg}
          onDownloadPNG={this.onDownloadPNG}
          onDownloadSVG={this.onDownloadSVG}
          onBotStyleChange={this.onBotStyleChange}
          onToggleCode={this.onToggleCode}
          onToggleImg={this.onToggleImg}
        />
        {displayComponentImg ? (
          <ComponentImg botStyle={botStyle}/>
        ) : null}
        {displayComponentCode ? (
          <ComponentCode botStyle={botStyle}/>
        ) : null}
        <canvas style={{ display: 'none' }}
                width={imageSize * 2}
                height={imageSize * 2}
                ref={this.onCanvasRef}
        />
      </main>
    )
  }

  private onBotRef = (ref: Bot) => {
    this.botRef = ref
  }

  private onCanvasRef = (ref: HTMLCanvasElement) => {
    this.canvasRef = ref
  }

  private onOptionValueChange = (key: string, value: string) => {
    const name = capitalizeFirstLetter(key)
    const handlerName = `onChange${name}`
    const updateHandler = this.props[handlerName] as (value: string) => void
    updateHandler(value)
  }

  private updateOptionContext (nextProps: Props) {
    this.optionContext.setData(nextProps as any)
  }

  private onBotStyleChange = (botStyle: BotStyle) => {
    this.props.onChangeBotStyle(botStyle)
  }

  private onSeed = () => {
    let values: { [index: string]: string } = {
      botStyle: this.props.botStyle,
    }

    const seed = window.prompt('Enter a seed')

    if (!isNil(seed)) {
      values = { ...SeededBottt(seed) }

      this.optionContext.setData(values)

      this.props.onChangeUrlQueryParams!(values, UrlUpdateTypes.push)
    }
  }

  private onRandom = () => {
    const { optionContext } = this
    let values: { [index: string]: string } = {
      botStyle: this.props.botStyle,
    }

    for (const option of optionContext.options) {
      if (option.key in values) {
        continue
      }
      const optionState = optionContext.getOptionState(option.key)!
      // Notice, when the app just launch and we didn't explore too much
      // options, some of these nested option is not added by the selector
      // yet, so we won't be able to select value for them. But as they
      // keep tapping random button, soon or later we will get all the
      // options. So it should be fine. Ideally we should find a better
      // way to collect all the options, but that's okay to just do it this
      // way for now.
      if (!optionState.options.length) {
        continue
      }
      values[option.key] = sample(optionState.options)!
    }
    this.optionContext.setData(values)

    this.props.onChangeUrlQueryParams!(values, UrlUpdateTypes.push)
  }

  private startRandomInterval = () => {
    if (!this.state.intervalOn) {
      const intervalReference = setInterval(this.onRandom, 200)

      this.setState({
        intervalOn: true,
        intervalStore: intervalReference,
      })
    }
  }

  private stopRandomInterval = () => {
    clearInterval(this.state.intervalStore as number)

    this.setState({
      intervalOn: false,
      intervalStore: 0,
    })
  }

  private onDownloadPNG = () => {
    const svgNode = ReactDOM.findDOMNode(this.botRef!)
    const canvas = this.canvasRef!
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const anyWindow = window as any
    const DOMURL = anyWindow.URL || anyWindow.webkitURL || window

    const data = svgNode.outerHTML
    const img = new Image()
    const svg = new Blob([data], { type: 'image/svg+xml' })
    const url = DOMURL.createObjectURL(svg)

    img.onload = () => {
      ctx.save()
      ctx.scale(2, 2)
      ctx.drawImage(img, 0, 0)
      ctx.restore()
      DOMURL.revokeObjectURL(url)
      this.canvasRef!.toBlob(imageBlob => {
        this.triggerDownload(imageBlob!, 'bottts.png')
      })
    }
    img.src = url
  }

  private onImagePreview = () => {
    const svgNode = ReactDOM.findDOMNode(this.botRef!)
    const canvas = this.canvasRef!
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const anyWindow = window as any
    const DOMURL = anyWindow.URL || anyWindow.webkitURL || window

    const data = svgNode.outerHTML
    const img = new Image()
    const svg = new Blob([data], { type: 'image/svg+xml' })
    const url = DOMURL.createObjectURL(svg)

    img.onload = () => {
      ctx.save()
      ctx.scale(2, 2)
      ctx.drawImage(img, 0, 0)
      ctx.restore()
      DOMURL.revokeObjectURL(url)
      this.triggerPreview()
    }

    img.src = url
  }

  private triggerPreview () {
    this.setState({ imageURI: this.canvasRef!.toDataURL() })
  }

  private onDownloadSVG = () => {
    const svgNode = ReactDOM.findDOMNode(this.botRef!)
    const data = svgNode.outerHTML
    const svg = new Blob([data], { type: 'image/svg+xml' })
    this.triggerDownload(svg, 'bottts.svg')
  }

  private triggerDownload (imageBlob: Blob, fileName: string) {
    FileSaver.saveAs(imageBlob, fileName)
  }

  private onToggleCode = () => {
    this.setState(state => ({
      ...state,
      displayComponentCode: !state.displayComponentCode,
      displayComponentImg: false,
    }))
  }

  private onToggleImg = () => {
    this.setState(state => ({
      ...state,
      displayComponentImg: !state.displayComponentImg,
      displayComponentCode: false,
    }))
  }
}

export default addUrlProps({ urlPropsQueryConfig })(Main)



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Main.tsx